import { useEffect, useMemo, useState } from 'react';
import { courses, lessons, lessonBlocks, vocabularyBank, type LessonSummary, type Course, type LessonBlockData, type VocabularyBankItem } from '../lib/api';
import LessonBlockView from '../components/LessonBlockView';
import { scoreLessonContent } from '../lib/contentQuality';

interface Props { onBack: () => void; }

function isOriginal(block: LessonBlockData) {
  return block.source === 'talkup-original' || block.source === 'talkup_original' || block.source === 'teacher-original' || block.source === 'admin-original';
}

function sourceLabel(block: LessonBlockData) {
  if (block.source === 'teacher-original') return 'Teacher Original';
  if (block.source === 'admin-original') return 'Admin Original';
  if (block.source === 'talkup-original' || block.source === 'talkup_original') return 'TalkUp Original';
  return 'Source material';
}

export default function MaterialsDashboard({ onBack }: Props) {
  const [courseList, setCourseList] = useState<Course[]>([]);
  const [lessonList, setLessonList] = useState<LessonSummary[]>([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<LessonBlockData[]>([]);
  const [lessonVocabulary, setLessonVocabulary] = useState<VocabularyBankItem[]>([]);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [blockFilter, setBlockFilter] = useState<'all' | 'source' | 'talkup_original' | 'exercise'>('all');
  const [unitFilter, setUnitFilter] = useState<number | 'all'>('all');
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);
  const [inspectorTab, setInspectorTab] = useState<'overview' | 'content' | 'structure'>('overview');

  useEffect(() => {
    courses().then((items) => {
      setCourseList(items);
      if (items[0]) setSelectedCourse(items[0].id);
    }).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load courses.'));
  }, []);

  useEffect(() => {
    if (!selectedCourse) return;
    setSelected(null);
    setSelectedBlock(null);
    setBlocks([]);
    lessons(selectedCourse).then((items) => {
      setLessonList(items);
      if (items[0]) setSelected(items[0].id);
    }).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load lessons.'));
  }, [selectedCourse]);

  useEffect(() => {
    if (!selected) return;
    setBlocks([]);
    setLessonVocabulary([]);
    setSelectedBlock(null);
    lessonBlocks(selected).then(setBlocks).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load lesson materials.'));
    vocabularyBank(selected).then(setLessonVocabulary).catch((e) => setMessage(e instanceof Error ? e.message : 'Could not load lesson vocabulary.'));
  }, [selected]);

  const units = useMemo(() => [...new Set(lessonList.map((item) => item.unit_number))].sort((a, b) => a - b), [lessonList]);

  const visibleLessons = useMemo(() => {
    const q = query.trim().toLowerCase();
    return lessonList.filter((item) => {
      const matchesUnit = unitFilter === 'all' || item.unit_number === unitFilter;
      const matchesQuery = !q || item.title.toLowerCase().includes(q);
      return matchesUnit && matchesQuery;
    });
  }, [lessonList, query, unitFilter]);

  const visibleBlocks = useMemo(() => blocks.filter((block) => {
    if (blockFilter === 'all') return true;
    if (blockFilter === 'talkup_original') return isOriginal(block);
    if (blockFilter === 'source') return !isOriginal(block);
    return block.block_type === 'exercise' || (() => {
      try { return JSON.parse(block.payload_json)?.type === 'exercise'; } catch { return false; }
    })();
  }), [blocks, blockFilter]);

  const lesson = lessonList.find((x) => x.id === selected);
  const inspectedBlock = blocks.find((block) => block.id === selectedBlock) || null;
  const inspectedPayload = useMemo(() => { if (!inspectedBlock) return null; try { return JSON.parse(inspectedBlock.payload_json) as Record<string, unknown>; } catch { return { text: inspectedBlock.payload_json }; } }, [inspectedBlock]);
  const inspectorKind = inspectedBlock?.block_type?.replace(/_/g, ' ') || '';
  const inspectorIsExercise = Boolean(inspectedBlock && (inspectedBlock.block_type === 'exercise' || (inspectedPayload && typeof inspectedPayload.exercise === 'object')));
  const inspectorMedia = inspectedPayload ? [inspectedPayload.audio_url, inspectedPayload.audioUrl, inspectedPayload.video_url, inspectedPayload.videoUrl, inspectedPayload.image_url, inspectedPayload.imageUrl].filter((value): value is string => typeof value === 'string' && Boolean(value.trim())) : [];
  const inspectorText = inspectedPayload && typeof inspectedPayload.text === 'string' ? inspectedPayload.text : inspectedPayload && typeof inspectedPayload.prompt === 'string' ? inspectedPayload.prompt : '';
  const inspectorExercise = inspectedPayload && inspectedPayload.exercise && typeof inspectedPayload.exercise === 'object' ? inspectedPayload.exercise as Record<string, unknown> : null;
  const inspectorAnswer = inspectorExercise?.answer ?? inspectedPayload?.answer;
  const inspectorTeacherNote = inspectorExercise?.teacherNote ?? inspectedPayload?.teacher_note;
  const course = courseList.find((x) => x.id === selectedCourse);
  const originalCount = blocks.filter(isOriginal).length;
  const sourceCount = blocks.length - originalCount;
  const vocabularyCount = lessonVocabulary.length;
  const exerciseCount = blocks.filter((block) => block.block_type === 'exercise' || (() => {
    try { return JSON.parse(block.payload_json)?.type === 'exercise'; } catch { return false; }
  })()).length;
  const lessonQuality = useMemo(() => scoreLessonContent(blocks, lessonVocabulary), [blocks, lessonVocabulary]);

  return <main className="teacher-page">
    <header className="teacher-header">
      <div>
        <p className="eyebrow">TALKUP · ADMIN · MATERIALS</p>
        <h1>Materials library</h1>
        <p>One readable library for the curriculum: original source content stays intact, while TalkUp adds structured interactive layers on top.</p>
      </div>
      <button className="back" onClick={onBack}>← School control</button>
    </header>

    {message && <div className="panel form-error">{message}</div>}

    <section className="panel material-toolbar">
      <label className="material-label">Course
        <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)}>
          {courseList.map((item) => <option key={item.id} value={item.id}>{item.language.toUpperCase()} · {item.level} · {item.title}</option>)}
        </select>
      </label>
      <label className="material-label">Unit
        <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}><option value="all">All units</option>{units.map((unit) => <option key={unit} value={unit}>Unit {unit}</option>)}</select>
      </label>
      <label className="material-label">Material
        <select value={blockFilter} onChange={(e) => setBlockFilter(e.target.value as typeof blockFilter)}>
          <option value="all">Everything</option><option value="source">Source material</option><option value="talkup_original">TalkUp / Teacher Originals</option><option value="exercise">Interactive exercises</option>
        </select>
      </label>
      <label className="material-label">Find a lesson
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Alphabet, grammar, travel…" />
      </label>
    </section>

    {lesson && <section className="material-stats">
      <article><b>{lessonList.length}</b><span>lessons in course</span></article>
      <article><b>{blocks.length}</b><span>blocks in lesson</span></article>
      <article><b>{sourceCount}</b><span>source blocks</span></article>
      <article><b>{originalCount}</b><span>TalkUp Originals</span></article>
      <article><b>{exerciseCount}</b><span>interactive exercises</span></article>\n      <article className={'material-quality ' + (lessonQuality.score >= 90 ? 'good' : lessonQuality.score >= 75 ? 'strong' : 'attention')}><b>{lessonQuality.score}%</b><span>content quality · {lessonQuality.label}</span></article>
    </section>}

    <section className="material-layout">
      <aside className="panel material-sidebar">
        <p className="eyebrow">{course?.language?.toUpperCase()} · {course?.level}</p>
        <h2>{course?.title || 'Course'}</h2>
        <div className="material-lesson-list">
          {visibleLessons.map((item) => <button className={selected === item.id ? 'material-lesson active' : 'material-lesson'} key={item.id} onClick={() => setSelected(item.id)}>
            <span>Unit {item.unit_number} · Lesson {item.lesson_number}</span><b>{item.title}</b><small>{Number(item.block_count || 0)} learning blocks</small>
          </button>)}
          {!visibleLessons.length && <p className="material-empty">No lessons match your search.</p>}
        </div>
      </aside>

      <section className="panel material-reader">
        {lesson ? <>
          <div className="material-reader-head">
            <div><p className="eyebrow">UNIT {lesson.unit_number} · LESSON {lesson.lesson_number}</p><h2>{lesson.title}</h2></div>
            <span>{visibleBlocks.length} shown</span>
          </div>
          <p className="material-reader-intro">Preview exactly as a learner would see the material. JSON, parser markers and internal payload structure stay out of the reading experience.</p>
          <div className="material-quality-panel">
            <div><span className="eyebrow">CONTENT FACTORY</span><strong>{lessonQuality.score}% quality</strong><small>{lessonQuality.counts.blocks} blocks · {lessonQuality.counts.vocabulary} words · {lessonQuality.counts.audio} audio · {lessonQuality.counts.visuals} visuals · {lessonQuality.counts.exercises} exercises</small></div>
            {lessonQuality.missing.length > 0 ? <div><b>Needs attention</b><span>{lessonQuality.missing.join(' · ')}</span></div> : <div><b>Ready</b><span>All core learning layers are present.</span></div>}
          </div>\n          <div className="material-quality-panel">\n            <div><span className="eyebrow">CONTENT FACTORY</span><strong>{lessonQuality.score}% quality</strong><small>{lessonQuality.counts.blocks} blocks · {lessonQuality.counts.vocabulary} words · {lessonQuality.counts.audio} audio · {lessonQuality.counts.visuals} visuals · {lessonQuality.counts.exercises} exercises</small></div>\n            {lessonQuality.missing.length > 0 ? <div><b>Needs attention</b><span>{lessonQuality.missing.join(' · ')}</span></div> : <div><b>Ready</b><span>All core learning layers are present.</span></div>}\n          </div>
          <div className="material-blocks">
            {visibleBlocks.map((block) => <article className={'material-preview-block ' + (selectedBlock === block.id ? 'selected' : '')} key={block.id} onClick={() => setSelectedBlock(block.id)}>
              <div className="block-heading"><span>{String(block.block_order).padStart(2,'0')} · {block.block_type.replace(/_/g, ' ')}</span><small>{sourceLabel(block)}</small></div>
              <h3>{block.title}</h3>
              <LessonBlockView block={block} interactive={false} />
            </article>)}
            {!visibleBlocks.length && <div className="material-empty">No materials match this filter.</div>}
            {inspectedBlock && <aside className="material-inspector">
              <div className="material-inspector-head">
                <div><p className="eyebrow">MATERIAL INSPECTOR</p><h3>{inspectedBlock.title}</h3><span>Block {String(inspectedBlock.block_order).padStart(2, '0')} · {inspectorKind}</span></div>
                <button onClick={() => setSelectedBlock(null)}>Close</button>
              </div>
              <div className="material-inspector-tabs">
                <button className={inspectorTab === 'overview' ? 'active' : ''} onClick={() => setInspectorTab('overview')}>Overview</button>
                <button className={inspectorTab === 'content' ? 'active' : ''} onClick={() => setInspectorTab('content')}>Learner content</button>
                <button className={inspectorTab === 'structure' ? 'active' : ''} onClick={() => setInspectorTab('structure')}>Structure</button>
              </div>
              {inspectorTab === 'overview' && <div className="material-inspector-grid">
                <article><span>VOCABULARY</span><b>{vocabularyCount} target words</b></article>
                <article><span>TYPE</span><b>{inspectorKind || 'content'}</b></article>
                <article><span>SOURCE</span><b>{sourceLabel(inspectedBlock)}</b></article>
                <article><span>INTERACTIVE</span><b>{inspectorIsExercise ? 'Yes · exercise engine' : 'No · learning block'}</b></article>
                <article><span>MEDIA</span><b>{inspectorMedia.length ? inspectorMedia.length + ' linked asset' + (inspectorMedia.length === 1 ? '' : 's') : 'No media linked'}</b></article>
              </div>}
              {inspectorTab === 'content' && <div className="material-inspector-content">
                <LessonBlockView block={inspectedBlock} interactive={false} />
                {inspectorMedia.length > 0 && <div className="material-media-list"><span>LINKED MEDIA</span>{inspectorMedia.map((url) => <code key={url}>{url}</code>)}</div>}
              </div>}
              {inspectorTab === 'structure' && <div className="material-structure">
                <div className="material-structure-row"><span>LESSON</span><b>{lesson?.title}</b></div>
                <div className="material-structure-row"><span>LESSON ID</span><code>{inspectedBlock.lesson_id}</code></div>
                <div className="material-structure-row"><span>BLOCK ID</span><code>{inspectedBlock.id}</code></div>
                <div className="material-structure-row"><span>BLOCK TYPE</span><code>{inspectedBlock.block_type}</code></div>
                {inspectorText && <div className="material-structure-row"><span>TEXT / PROMPT</span><p>{inspectorText}</p></div>}
                {inspectorExercise && <div className="material-exercise-config">
                  <span>EXERCISE CONFIGURATION</span>
                  <div><b>{String(inspectorExercise.type || 'exercise')}</b><small>{String(inspectorExercise.difficulty || 'A0')} · {String(inspectorExercise.source || 'TalkUp')}</small></div>
                  {typeof inspectorAnswer === 'string' && inspectorAnswer && <p><strong>Answer key:</strong> {inspectorAnswer}</p>}
                  {typeof inspectorTeacherNote === 'string' && inspectorTeacherNote && <p><strong>Teacher note:</strong> {inspectorTeacherNote}</p>}
                </div>}
              </div>}
              {lessonVocabulary.length > 0 && <section className="material-vocabulary-bank"><div className="material-vocabulary-head"><div><span className="eyebrow">VOCABULARY BANK</span><h4>{vocabularyCount} lesson words</h4></div><small>Ready for revision and teacher use</small></div><div className="material-vocabulary-grid">{lessonVocabulary.map((item) => <article key={item.id}><b>{item.word}</b><span>{item.translation}</span>{item.example && <p>{item.example}</p>}</article>)}</div></section>}
              <div className="material-inspector-footer">
                <span>Source, structure and learner rendering stay connected in one place.</span>
                <button onClick={() => setInspectorTab('content')}>Open learner preview</button>
              </div>
            </aside>}
          </div>
        </> : <div className="material-empty">Select a lesson to preview its content.</div>}
      </section>
    </section>
  </main>;
}
